import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, X, Plus, Trash2, Check, RefreshCw, Pencil, ImagePlus, ChevronDown, Search } from 'lucide-react';
import {
  KendamaBrand,
  KendamaModel,
  KendamaCategory,
  KendamaType,
  kendamaCategoryLabels,
  kendamaTypeLabels,
} from '../kendamaCatalog';
import { KendamaSubmission, KendamaSubmissionStatus } from '../types';
import {
  fetchAdminKendamaData,
  updateKendamaSubmissionStatus,
  saveKendamaCatalog,
  readFileAsDataUrl,
} from '../kendamaArchiveApi';

interface KendamaArchiveAdminProps {
  onBack: () => void;
  onCatalogChanged?: (catalog: KendamaBrand[]) => void;
}

const categoryOptions: KendamaCategory[] = ['entry', 'competition', 'signature', 'limited', 'standard', 'premium'];
const typeOptions: KendamaType[] = ['ken', 'tama', 'bundle', 'novelty'];

// 单张图片上限与服务端保持一致；总张数按请求体大小限制收敛。
const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const statusText: Record<KendamaSubmissionStatus, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已拒绝',
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'item';

const statusBadgeClassName: Record<KendamaSubmissionStatus, string> = {
  pending: 'border-amber-400/30 bg-amber-500/10 text-amber-200',
  approved: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
  rejected: 'border-rose-400/30 bg-rose-500/10 text-rose-200',
};

const inputClassName =
  'w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-cyan-300/50 focus:bg-white/[0.07]';

const labelClassName = 'mb-1 block text-xs text-zinc-400';

export const KendamaArchiveAdmin: React.FC<KendamaArchiveAdminProps> = ({ onBack, onCatalogChanged }) => {
  const [tab, setTab] = useState<'submissions' | 'catalog'>('catalog');
  const [submissions, setSubmissions] = useState<KendamaSubmission[]>([]);
  const [catalog, setCatalog] = useState<KendamaBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingModel, setEditingModel] = useState<{ brandId: string; modelId: string } | null>(null);
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());
  const [brandSearch, setBrandSearch] = useState('');
  const [brandForm, setBrandForm] = useState({
    name: '',
    nameEn: '',
    nameZh: '',
    nameJa: '',
    country: '',
    countryZh: '',
    countryJa: '',
    accent: '#22d3ee',
    descriptionZh: '',
  });

  const [form, setForm] = useState({
    brandName: '',
    modelName: '',
    nameZh: '',
    nameJa: '',
    type: 'ken' as KendamaType,
    material: 'Beech',
    paint: '',
    category: 'standard' as KendamaCategory,
    tags: '',
    description: '',
    images: [] as string[],
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminKendamaData();
      setSubmissions(data.submissions);
      setCatalog(data.catalog);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载管理员数据失败。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatus = async (id: string, status: KendamaSubmissionStatus) => {
    setMessage('');
    setError('');
    try {
      await updateKendamaSubmissionStatus(id, status);
      setSubmissions((previous) =>
        previous.map((item) => (item.id === id ? { ...item, status } : item)),
      );
      setMessage(status === 'approved' ? '已通过该反馈。' : '已拒绝该反馈。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新状态失败。');
    }
  };

  const handleDeleteSubmission = async (id: string) => {
    // 用户提交暂以“拒绝”作为清理方式，保留记录便于回溯。
    await handleStatus(id, 'rejected');
  };

  const resetForm = () => {
    setForm({
      brandName: '',
      modelName: '',
      nameZh: '',
      nameJa: '',
      type: 'ken',
      material: 'Beech',
      paint: '',
      category: 'standard',
      tags: '',
      description: '',
      images: [],
    });
    setEditingModel(null);
    setShowForm(false);
  };

  const handleImageFilesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    if (files.some((file) => file.size > MAX_IMAGE_SIZE)) {
      setError('单张图片请控制在 5MB 以内。');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const room = MAX_IMAGES - form.images.length;
    if (room <= 0) {
      setError(`最多上传 ${MAX_IMAGES} 张图片。`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const dataUrls = await Promise.all(files.slice(0, room).map((file) => readFileAsDataUrl(file)));
      setForm((previous) => ({ ...previous, images: [...previous.images, ...dataUrls] }));
      setError(files.length > room ? `最多上传 ${MAX_IMAGES} 张图片。` : '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '图片读取失败，请重试。');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImageAt = (index: number) => {
    setForm((previous) => ({ ...previous, images: previous.images.filter((_, i) => i !== index) }));
  };

  const handleSubmitCatalogEntry = async () => {
    const brandName = form.brandName.trim();
    const modelName = form.modelName.trim();
    if (!brandName || !modelName) {
      setError('请填写品牌名和剑玉名称。');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    const type = form.type;
    const tags = form.tags
      .split(/[,，]/)
      .map((tag) => tag.trim())
      .filter(Boolean);
    const images = form.images.filter(Boolean);
    // 第一张图作为封面展示。
    const image = images[0];

    const nextCatalog = catalog.map((brand) => ({ ...brand, models: [...brand.models] }));

    if (editingModel) {
      const brandIndex = nextCatalog.findIndex((brand) => brand.id === editingModel.brandId);
      if (brandIndex < 0) {
        setError('未找到要编辑的剑玉。');
        setSaving(false);
        return;
      }
      const modelIndex = nextCatalog[brandIndex].models.findIndex((model) => model.id === editingModel.modelId);
      if (modelIndex < 0) {
        setError('未找到要编辑的剑玉。');
        setSaving(false);
        return;
      }
      const original = nextCatalog[brandIndex].models[modelIndex];
      nextCatalog[brandIndex].models[modelIndex] = {
        ...original,
        name: modelName,
        nameZh: form.nameZh.trim() || modelName,
        nameJa: form.nameJa.trim() || modelName,
        type,
        category: form.category,
        material: form.material.trim() || 'Beech',
        paint: form.paint.trim() || undefined,
        image,
        images,
        tags,
        description: { zh: form.description.trim(), en: '', ja: '' },
      };

      try {
        const persisted = await saveKendamaCatalog(nextCatalog);
        setCatalog(persisted);
        onCatalogChanged?.(persisted);
        setMessage(`已保存「${modelName}」的修改。`);
        resetForm();
      } catch (err) {
        setError(err instanceof Error ? err.message : '保存剑玉目录失败。');
      } finally {
        setSaving(false);
      }
      return;
    }

    const model: KendamaModel = {
      id: `kendama-model-${slugify(modelName)}-${Date.now()}`,
      name: modelName,
      nameZh: form.nameZh.trim() || modelName,
      nameJa: form.nameJa.trim() || modelName,
      type,
      shape: type === 'ken' ? 'Custom' : 'Round',
      category: form.category,
      material: form.material.trim() || 'Beech',
      paint: form.paint.trim() || undefined,
      image,
      images,
      tags,
      description: { zh: form.description.trim(), en: '', ja: '' },
    };

    const existingIndex = nextCatalog.findIndex(
      (brand) => brand.name.toLowerCase() === brandName.toLowerCase(),
    );

    if (existingIndex >= 0) {
      nextCatalog[existingIndex].models.push(model);
    } else {
      nextCatalog.push({
        id: `kendama-brand-${slugify(brandName)}-${Date.now()}`,
        name: brandName,
        nameZh: brandName,
        nameJa: brandName,
        country: '',
        countryZh: '',
        countryJa: '',
        accent: '#22d3ee',
        description: { zh: '', en: '', ja: '' },
        models: [model],
      });
    }

    try {
      const persisted = await saveKendamaCatalog(nextCatalog);
      setCatalog(persisted);
      onCatalogChanged?.(persisted);
      setMessage(`已新增「${brandName} · ${modelName}」。`);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存剑玉目录失败。');
    } finally {
      setSaving(false);
    }
  };

  const openNewModel = (brand: KendamaBrand) => {
    setEditingModel(null);
    setForm({
      brandName: brand.name,
      modelName: '',
      nameZh: '',
      nameJa: '',
      type: 'ken',
      material: 'Beech',
      paint: '',
      category: 'standard',
      tags: '',
      description: '',
      images: [],
    });
    setError('');
    setMessage('');
    setShowForm(true);
  };

  const handleEditCatalogModel = (brandId: string, modelId: string) => {
    const brand = catalog.find((item) => item.id === brandId);
    const model = brand?.models.find((item) => item.id === modelId);
    if (!brand || !model) return;

    setEditingModel({ brandId, modelId });
    setForm({
      brandName: brand.name,
      modelName: model.name,
      nameZh: model.nameZh ?? '',
      nameJa: model.nameJa ?? '',
      type: model.type,
      material: model.material ?? 'Beech',
      paint: model.paint ?? '',
      category: model.category,
      tags: Array.isArray(model.tags) ? model.tags.join(', ') : '',
      description: model.description?.zh ?? '',
      images: model.images?.length ? [...model.images] : model.image ? [model.image] : [],
    });
    setError('');
    setMessage('');
    setShowForm(true);
  };

  const handleDeleteCatalogModel = async (brandId: string, modelId: string) => {
    setSaving(true);
    setError('');
    setMessage('');
    const nextCatalog = catalog
      .map((brand) => ({ ...brand, models: brand.models.filter((model) => model.id !== modelId) }))
      .filter((brand) => brand.models.length > 0);

    try {
      const persisted = await saveKendamaCatalog(nextCatalog);
      setCatalog(persisted);
      onCatalogChanged?.(persisted);
      setMessage('已删除该剑玉条目。');
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除剑玉条目失败。');
    } finally {
      setSaving(false);
    }
  };

  const resetBrandForm = () => {
    setBrandForm({
      name: '',
      nameEn: '',
      nameZh: '',
      nameJa: '',
      country: '',
      countryZh: '',
      countryJa: '',
      accent: '#22d3ee',
      descriptionZh: '',
    });
    setEditingBrandId(null);
    setShowBrandForm(false);
  };

  const openNewBrand = () => {
    resetBrandForm();
    setShowBrandForm(true);
  };

  const toggleBrand = (brandId: string) => {
    setExpandedBrands((previous) => {
      if (previous.has(brandId)) {
        const next = new Set(previous);
        next.delete(brandId);
        return next;
      }
      return new Set([brandId]);
    });
  };

  const handleEditBrand = (brandId: string) => {
    const brand = catalog.find((item) => item.id === brandId);
    if (!brand) return;
    setEditingBrandId(brandId);
    setBrandForm({
      name: brand.name,
      nameEn: brand.nameEn ?? '',
      nameZh: brand.nameZh ?? '',
      nameJa: brand.nameJa ?? '',
      country: brand.country ?? '',
      countryZh: brand.countryZh ?? '',
      countryJa: brand.countryJa ?? '',
      accent: brand.accent ?? '#22d3ee',
      descriptionZh: brand.description?.zh ?? '',
    });
    setError('');
    setMessage('');
    setShowBrandForm(true);
  };

  const handleSubmitBrand = async () => {
    const name = brandForm.name.trim();
    if (!name) {
      setError('请填写品牌名。');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    const nextCatalog = catalog.map((brand) => ({ ...brand, models: [...brand.models] }));
    const descriptionZh = brandForm.descriptionZh.trim();

    if (editingBrandId) {
      const index = nextCatalog.findIndex((brand) => brand.id === editingBrandId);
      if (index < 0) {
        setError('未找到要编辑的品牌。');
        setSaving(false);
        return;
      }
      const original = nextCatalog[index];
      nextCatalog[index] = {
        ...original,
        name,
        nameEn: brandForm.nameEn.trim() || undefined,
        nameZh: brandForm.nameZh.trim() || name,
        nameJa: brandForm.nameJa.trim() || name,
        country: brandForm.country.trim(),
        countryZh: brandForm.countryZh.trim(),
        countryJa: brandForm.countryJa.trim(),
        accent: brandForm.accent.trim() || '#22d3ee',
        description: { ...original.description, zh: descriptionZh },
      };
    } else {
      nextCatalog.push({
        id: `kendama-brand-${slugify(name)}-${Date.now()}`,
        name,
        nameEn: brandForm.nameEn.trim() || undefined,
        nameZh: brandForm.nameZh.trim() || name,
        nameJa: brandForm.nameJa.trim() || name,
        country: brandForm.country.trim(),
        countryZh: brandForm.countryZh.trim(),
        countryJa: brandForm.countryJa.trim(),
        accent: brandForm.accent.trim() || '#22d3ee',
        description: { zh: descriptionZh, en: '', ja: '' },
        models: [],
      });
    }

    try {
      const persisted = await saveKendamaCatalog(nextCatalog);
      setCatalog(persisted);
      onCatalogChanged?.(persisted);
      setMessage(editingBrandId ? `已保存「${name}」的品牌信息。` : `已新增品牌「${name}」。`);
      resetBrandForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存品牌失败。');
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = useMemo(
    () => submissions.filter((item) => item.status === 'pending').length,
    [submissions],
  );

  const filteredBrands = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    if (!query) return catalog;
    return catalog.filter((brand) => {
      const haystack = [brand.name, brand.nameEn, brand.nameZh, brand.nameJa]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [catalog, brandSearch]);

  return (
    <div className="relative isolate flex h-full min-h-0 w-full flex-col overflow-hidden text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_transparent_28%),linear-gradient(180deg,_rgba(12,14,20,0.98),_rgba(5,6,10,1))]"
      />

      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 py-3 md:px-6 md:py-5 custom-scrollbar">
        {/* Header */}
        <div className="relative flex min-h-[44px] shrink-0 items-center justify-center px-12 text-center">
          <button
            onClick={onBack}
            className="absolute left-0 p-2 text-white/50 transition-colors hover:text-white"
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="font-pingfang text-lg font-bold tracking-[0.12em] text-cyan-300 drop-shadow-[0_0_14px_rgba(34,211,238,0.38)] md:text-2xl">
            万剑阁管理员
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setTab('catalog')}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
              tab === 'catalog'
                ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            剑玉目录
          </button>
          <button
            type="button"
            onClick={() => setTab('submissions')}
            className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
              tab === 'submissions'
                ? 'border-cyan-300/60 bg-cyan-500/20 text-cyan-100 shadow-[0_0_14px_rgba(34,211,238,0.24)]'
                : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 hover:bg-white/10'
            }`}
          >
            用户提交
            {pendingCount > 0 && (
              <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[10px] leading-none text-amber-200">
                {pendingCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="ml-auto flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            刷新
          </button>
        </div>

        {(message || error) && (
          <div
            className={`shrink-0 rounded-xl border px-3 py-2 text-xs ${
              error ? 'border-rose-400/30 bg-rose-500/10 text-rose-200' : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
            }`}
          >
            {error || message}
          </div>
        )}

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-sm text-zinc-500">加载中…</div>
        ) : tab === 'submissions' ? (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {submissions.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-zinc-500">暂无用户提交。</div>
            ) : (
              submissions.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-white/10 bg-black/35 p-3 md:p-4"
                >
                  <div className="flex items-start gap-3">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.modelName}
                        className="h-16 w-16 shrink-0 rounded-xl border border-white/10 bg-white/5 object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[10px] text-zinc-500">
                        无图片
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-orbitron text-sm font-bold text-white">{item.modelName}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusBadgeClassName[item.status]}`}>
                          {statusText[item.status]}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-zinc-400">
                        品牌：<span className="text-zinc-200">{item.brandName}</span>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-zinc-500">
                        {item.userDisplayName} · {new Date(item.createdAt).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1.5">
                      {item.status === 'pending' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleStatus(item.id, 'approved')}
                            className="flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200 transition-colors hover:bg-emerald-500/20"
                          >
                            <Check size={13} />
                            通过
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStatus(item.id, 'rejected')}
                            className="flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-500/10 px-3 py-1 text-xs text-rose-200 transition-colors hover:bg-rose-500/20"
                          >
                            <X size={13} />
                            拒绝
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDeleteSubmission(item.id)}
                          className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-zinc-400 transition-colors hover:bg-white/10"
                        >
                          <Trash2 size={13} />
                          删除
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  className={`${inputClassName} pl-9`}
                  value={brandSearch}
                  onChange={(event) => setBrandSearch(event.target.value)}
                  placeholder="搜索品牌…"
                />
              </div>
              <button
                type="button"
                onClick={openNewBrand}
                className="flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-cyan-300/40 bg-cyan-500/5 px-4 py-2.5 text-xs font-medium text-cyan-200 transition-colors hover:bg-cyan-500/10"
              >
                <Plus size={15} />
                新建品牌
              </button>
            </div>

            {showForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
                <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0c0e14] p-4 shadow-[0_0_40px_rgba(34,211,238,0.15)] custom-scrollbar">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-medium text-white">
                      {editingModel ? '编辑剑玉' : '新增剑玉'}
                    </div>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="p-1 text-white/50 transition-colors hover:text-white"
                      aria-label="关闭"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelClassName}>品牌名 *</label>
                    <input
                      className={inputClassName}
                      value={form.brandName}
                      onChange={(event) => setForm((previous) => ({ ...previous, brandName: event.target.value }))}
                      placeholder="如 Sweets Kendamas"
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>英文名 *</label>
                    <input
                      className={inputClassName}
                      value={form.modelName}
                      onChange={(event) => setForm((previous) => ({ ...previous, modelName: event.target.value }))}
                      placeholder="如 Prime"
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>中文名</label>
                    <input
                      className={inputClassName}
                      value={form.nameZh}
                      onChange={(event) => setForm((previous) => ({ ...previous, nameZh: event.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>日文名</label>
                    <input
                      className={inputClassName}
                      value={form.nameJa}
                      onChange={(event) => setForm((previous) => ({ ...previous, nameJa: event.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>类型</label>
                    <select
                      className={inputClassName}
                      value={form.type}
                      onChange={(event) => setForm((previous) => ({ ...previous, type: event.target.value as KendamaType }))}
                    >
                      {typeOptions.map((type) => (
                        <option key={type} value={type} className="bg-[#0c0e14]">
                          {kendamaTypeLabels[type].zh}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClassName}>分类</label>
                    <select
                      className={inputClassName}
                      value={form.category}
                      onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value as KendamaCategory }))}
                    >
                      {categoryOptions.map((category) => (
                        <option key={category} value={category} className="bg-[#0c0e14]">
                          {kendamaCategoryLabels[category].zh}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClassName}>材质</label>
                    <input
                      className={inputClassName}
                      value={form.material}
                      onChange={(event) => setForm((previous) => ({ ...previous, material: event.target.value }))}
                      placeholder="如 Beech / Maple"
                    />
                  </div>
                  {form.type === 'tama' && (
                    <div>
                      <label className={labelClassName}>漆面</label>
                      <input
                        className={inputClassName}
                        value={form.paint}
                        onChange={(event) => setForm((previous) => ({ ...previous, paint: event.target.value }))}
                        placeholder="如 Matte"
                      />
                    </div>
                  )}
                  <div>
                    <label className={labelClassName}>标签（逗号分隔）</label>
                    <input
                      className={inputClassName}
                      value={form.tags}
                      onChange={(event) => setForm((previous) => ({ ...previous, tags: event.target.value }))}
                      placeholder="如 入门, 经典"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClassName}>描述</label>
                    <textarea
                      className={`${inputClassName} min-h-[72px] resize-none`}
                      value={form.description}
                      onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClassName}>
                      图片（可选，最多 {MAX_IMAGES} 张；第一张作为封面，详情页可左右滑动查看）
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleImageFilesChange}
                    />
                    {form.images.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {form.images.map((image, index) => (
                            <div
                              key={`${index}-${image.slice(0, 24)}`}
                              className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-white/5"
                            >
                              <img src={image} alt={`预览 ${index + 1}`} className="h-full w-full object-cover" />
                              {index === 0 && (
                                <span className="absolute left-1 top-1 rounded-full bg-cyan-500/80 px-1.5 py-0.5 text-[10px] leading-none text-white">
                                  封面
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeImageAt(index)}
                                className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-rose-200 transition-colors hover:bg-rose-500/70 hover:text-white"
                                aria-label="移除图片"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                        {form.images.length < MAX_IMAGES && (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1.5 self-start rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10"
                          >
                            <ImagePlus size={14} />
                            继续添加图片
                          </button>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-cyan-300/40 bg-cyan-500/5 px-3 py-3 text-xs font-medium text-cyan-200 transition-colors hover:bg-cyan-500/10"
                      >
                        <ImagePlus size={16} />
                        上传图片
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitCatalogEntry}
                    disabled={saving}
                    className="rounded-full border border-cyan-300/60 bg-cyan-500/20 px-4 py-1.5 text-xs font-medium text-cyan-100 transition-colors hover:bg-cyan-500/25 disabled:opacity-50"
                  >
                    {saving ? '保存中…' : editingModel ? '保存修改' : '保存到目录'}
                  </button>
                </div>
                </div>
              </div>
            )}

            {showBrandForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
                <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#0c0e14] p-4 shadow-[0_0_40px_rgba(34,211,238,0.15)] custom-scrollbar">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-sm font-medium text-white">
                      {editingBrandId ? '编辑品牌信息' : '新建品牌'}
                    </div>
                    <button
                      type="button"
                      onClick={resetBrandForm}
                      className="p-1 text-white/50 transition-colors hover:text-white"
                      aria-label="关闭"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelClassName}>品牌名 *</label>
                      <input
                        className={inputClassName}
                        value={brandForm.name}
                        onChange={(event) => setBrandForm((previous) => ({ ...previous, name: event.target.value }))}
                        placeholder="如 Sweets Kendamas"
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>品牌属地（英文）</label>
                      <input
                        className={inputClassName}
                        value={brandForm.country}
                        onChange={(event) => setBrandForm((previous) => ({ ...previous, country: event.target.value }))}
                        placeholder="如 USA"
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>品牌属地（中文）</label>
                      <input
                        className={inputClassName}
                        value={brandForm.countryZh}
                        onChange={(event) => setBrandForm((previous) => ({ ...previous, countryZh: event.target.value }))}
                        placeholder="如 美国"
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>品牌属地（日文）</label>
                      <input
                        className={inputClassName}
                        value={brandForm.countryJa}
                        onChange={(event) => setBrandForm((previous) => ({ ...previous, countryJa: event.target.value }))}
                      />
                    </div>
                    <div>
                      <label className={labelClassName}>强调色</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={brandForm.accent}
                          onChange={(event) => setBrandForm((previous) => ({ ...previous, accent: event.target.value }))}
                          className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-white/5"
                        />
                        <input
                          className={inputClassName}
                          value={brandForm.accent}
                          onChange={(event) => setBrandForm((previous) => ({ ...previous, accent: event.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClassName}>品牌描述（中文）</label>
                      <textarea
                        className={`${inputClassName} min-h-[72px] resize-none`}
                        value={brandForm.descriptionZh}
                        onChange={(event) => setBrandForm((previous) => ({ ...previous, descriptionZh: event.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={resetBrandForm}
                      className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-white/10"
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitBrand}
                      disabled={saving}
                      className="rounded-full border border-cyan-300/60 bg-cyan-500/20 px-4 py-1.5 text-xs font-medium text-cyan-100 transition-colors hover:bg-cyan-500/25 disabled:opacity-50"
                    >
                      {saving ? '保存中…' : editingBrandId ? '保存修改' : '保存品牌'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {filteredBrands.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-zinc-500">
                {brandSearch.trim() ? '未找到匹配的品牌。' : '暂无自定义剑玉条目。'}
              </div>
            ) : (
              filteredBrands.map((brand) => (
                <div key={brand.id} className="rounded-2xl border border-white/10 bg-black/35 p-3 md:p-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleBrand(brand.id)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span className="h-6 w-1 shrink-0 rounded-full" style={{ backgroundColor: brand.accent }} />
                      <span className="font-orbitron text-sm font-bold text-white">{brand.name}</span>
                      <span className="text-xs text-zinc-500">（{brand.models.length} 款）</span>
                      <ChevronDown
                        size={16}
                        className={`ml-auto shrink-0 text-zinc-500 transition-transform ${expandedBrands.has(brand.id) ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => openNewModel(brand)}
                      disabled={saving}
                      className="flex shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-cyan-200 disabled:opacity-50"
                    >
                      <Plus size={13} />
                      新增剑玉
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditBrand(brand.id)}
                      disabled={saving}
                      className="ml-auto flex shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-cyan-200 disabled:opacity-50"
                    >
                      <Pencil size={13} />
                      编辑品牌
                    </button>
                  </div>
                  {expandedBrands.has(brand.id) && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {brand.models.map((model) => (
                        <div
                          key={model.id}
                          className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm text-white">{model.name}</div>
                            <div className="text-[11px] text-zinc-500">
                              {kendamaTypeLabels[model.type].zh} · {model.material}
                              {model.paint ? ` · ${model.paint}` : ''}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleEditCatalogModel(brand.id, model.id)}
                              disabled={saving}
                              className="shrink-0 rounded-full border border-white/10 p-1.5 text-zinc-500 transition-colors hover:border-cyan-300/40 hover:text-cyan-200 disabled:opacity-50"
                              aria-label="编辑"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCatalogModel(brand.id, model.id)}
                              disabled={saving}
                              className="shrink-0 rounded-full border border-white/10 p-1.5 text-zinc-500 transition-colors hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-50"
                              aria-label="删除"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
